import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, Archive, ArchiveRestore, FileBarChart, Fuel, Gauge, HandCoins, Handshake, Pencil, Plus, ShieldCheck, Trash2, Truck, Wrench } from 'lucide-react';
import { Button, FormField, KPICard, Modal, PageHeader, Skeleton, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type EnginCategorie,
  type EnginDTO,
  type EnginInput,
  type EnginStatut,
  CATEGORIE_LABEL,
  ENGIN_CATEGORIES,
  ENGIN_STATUTS,
  createEngin,
  deleteEngin,
  getEnginsDashboard,
  listEngins,
  updateEngin,
} from '@/services/engins';
import { listTiers } from '@/services/tiers';
import { toast } from '@/store/toastStore';
import type { StatusKey } from '@/types';
import { formatDZDCompact } from '@/utils/algeria';

const STATUT_UI: Record<EnginStatut, { label: string; status: StatusKey }> = {
  DISPONIBLE: { label: 'Disponible', status: 'done' },
  AFFECTE: { label: 'Affecté', status: 'working' },
  MAINTENANCE: { label: 'Maintenance', status: 'review' },
  HORS_SERVICE: { label: 'Hors service', status: 'stuck' },
};

const toForm = (e: EnginDTO): EnginInput => ({
  code: e.code, designation: e.designation, categorie: e.categorie, marque: e.marque, modele: e.modele, immatriculation: e.immatriculation,
  annee_fabrication: e.annee_fabrication, date_acquisition: e.date_acquisition, valeur_acquisition: e.valeur_acquisition,
  duree_amortissement_ans: e.duree_amortissement_ans, type: e.type, statut: e.statut, compteur_actuel: e.compteur_actuel,
  unite_compteur: e.unite_compteur, prochaine_maintenance_date: e.prochaine_maintenance_date, prochaine_maintenance_km: e.prochaine_maintenance_km,
  loueur_id: e.loueur_id, cout_location_jour: e.cout_location_jour, location_date_debut: e.location_date_debut, location_date_fin: e.location_date_fin,
});

const num = (v: unknown) => (v === '' || v == null ? null : Number(v));

export function EnginsPage() {
  const perms = usePerms('engins');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EnginDTO | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [filtreCat, setFiltreCat] = useState<EnginCategorie | ''>('');
  const { data, isLoading } = useQuery({ queryKey: ['engins', { showArchived, filtreCat }], queryFn: () => listEngins({ archives: showArchived, categorie: filtreCat || undefined }) });
  const { data: dash } = useQuery({ queryKey: ['engins-dashboard'], queryFn: getEnginsDashboard });
  const { data: tiers } = useQuery({ queryKey: ['tiers', 'all'], queryFn: () => listTiers({ size: 200 }), enabled: open });
  const { register, handleSubmit, reset, watch } = useForm<EnginInput>({ defaultValues: { type: 'PROPRE', statut: 'DISPONIBLE', unite_compteur: 'H', categorie: 'ENGIN_TP' } });
  const typeEngin = watch('type');

  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['engins'] }); queryClient.invalidateQueries({ queryKey: ['engins-dashboard'] }); };
  const openCreate = () => { setEditing(null); reset({ type: 'PROPRE', statut: 'DISPONIBLE', unite_compteur: 'H', categorie: 'ENGIN_TP', code: '', designation: '' }); setOpen(true); };
  // Machine louée à un tiers : même formulaire, pré-réglé sur « Loué » (loueur + tarif demandés).
  const openCreateLouee = () => { setEditing(null); reset({ type: 'LOUE', statut: 'DISPONIBLE', unite_compteur: 'H', categorie: 'ENGIN_TP', code: '', designation: '' }); setOpen(true); };
  const openEdit = (e: EnginDTO) => { setEditing(e); reset(toForm(e)); setOpen(true); };

  const saveMutation = useMutation({
    mutationFn: (v: EnginInput) => {
      const payload = {
        ...v,
        annee_fabrication: num(v.annee_fabrication), valeur_acquisition: num(v.valeur_acquisition),
        duree_amortissement_ans: num(v.duree_amortissement_ans), compteur_actuel: Number(v.compteur_actuel) || 0,
        conso_reference_lh: num(v.conso_reference_lh),
        prochaine_maintenance_km: num(v.prochaine_maintenance_km),
        loueur_id: v.type === 'LOUE' ? (v.loueur_id || null) : null,
        cout_location_jour: v.type === 'LOUE' ? num(v.cout_location_jour) : null,
      };
      return editing ? updateEngin(editing.id, payload) : createEngin(payload);
    },
    onSuccess: () => { invalidate(); toast.success(editing ? 'Engin modifié.' : 'Engin ajouté au parc.'); reset(); setOpen(false); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateEngin(id, { is_archived: archived }),
    onSuccess: (_d, v) => { invalidate(); toast.success(v.archived ? 'Engin archivé.' : 'Engin restauré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteEngin(id),
    onSuccess: () => { invalidate(); toast.success('Engin supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const act = (e: React.MouseEvent, fn: () => void) => { e.preventDefault(); e.stopPropagation(); fn(); };

  return (
    <>
      <PageHeader
        title="Parc & matériel"
        subtitle="Engins TP et véhicules : disponibilité, carburant, maintenance, affectation et assurances"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Parc & matériel' }]}
        actions={
          <div className="flex items-center gap-3">
            <select value={filtreCat} onChange={(e) => setFiltreCat(e.target.value as EnginCategorie | '')} className="h-10 rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="">Toutes catégories</option>
              {ENGIN_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORIE_LABEL[c]}</option>)}
            </select>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
              <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Archivés
            </label>
            <Button onClick={() => navigate('/engins/locations')}><HandCoins /> Location d'engins</Button>
            <Button variant="secondary" onClick={() => navigate('/engins/etats')}><FileBarChart /> États de location</Button>
            {perms.canAjouter && <Button variant="secondary" onClick={openCreateLouee} title="Enregistrer une machine louée à un tiers, pour en pointer les journées"><Handshake /> Louer une machine</Button>}
            {perms.canAjouter && <Button onClick={openCreate}><Plus /> Nouveau</Button>}
          </div>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 xl:grid-cols-5">
        <KPICard title="Parc total" value={String(dash?.total ?? 0)} icon={Truck} accent="#1a1a2e" />
        <KPICard title="Disponibilité" value={`${dash?.taux_disponibilite ?? 0} %`} icon={Activity} accent="#00C875" />
        <KPICard title="Maintenances J-7" value={String(dash?.maintenances_proches ?? 0)} icon={Wrench} accent="#F97316" />
        <KPICard title="Échéances J-30" value={String(dash?.echeances_proches ?? 0)} icon={ShieldCheck} accent="#E2445C" />
        <KPICard title="Carburant (mois)" value={formatDZDCompact(dash?.carburant_mois ?? 0)} icon={Fuel} accent="#64748b" />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-lg" />)}</div>
      ) : data && data.items.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((e) => {
            const ui = STATUT_UI[e.statut];
            return (
              <div
                key={e.id}
                onClick={() => navigate(`/engins/${e.id}`)}
                className="group flex cursor-pointer flex-col gap-3 rounded-lg border border-border bg-surface-secondary p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <span className="flex size-11 items-center justify-center rounded-lg bg-surface-hover text-brand-primary"><Truck className="size-6" /></span>
                  <StatusBadge status={ui.status} label={ui.label} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-content-muted">{e.code} · {CATEGORIE_LABEL[e.categorie]} · {e.type === 'LOUE' ? 'Loué' : 'Propre'}</p>
                  <h3 className="font-bold text-content-primary group-hover:text-brand-primary">{e.designation}</h3>
                  <p className="text-xs text-content-secondary">{[e.marque, e.modele].filter(Boolean).join(' ') || '—'}{e.immatriculation ? ` · ${e.immatriculation}` : ''}</p>
                </div>
                <p className="flex items-center gap-1.5 text-xs text-content-muted"><Gauge className="size-3.5" /> {Number(e.compteur_actuel).toLocaleString('fr-DZ')} {e.unite_compteur}{e.prochaine_maintenance_date ? ` · révision ${e.prochaine_maintenance_date}` : ''}</p>
                <div className="mt-auto flex items-center justify-end gap-1 border-t border-border pt-2">
                  {showArchived ? (
                    perms.canArchiver && <button onClick={(ev) => act(ev, () => archiveMutation.mutate({ id: e.id, archived: false }))} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-status-done" aria-label="Restaurer"><ArchiveRestore className="size-4" /></button>
                  ) : (
                    <>
                      {perms.canModifier && <button onClick={(ev) => act(ev, () => openEdit(e))} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-brand-primary" aria-label="Modifier"><Pencil className="size-4" /></button>}
                      {perms.canArchiver && <button onClick={(ev) => act(ev, () => archiveMutation.mutate({ id: e.id, archived: true }))} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-content-primary" aria-label="Archiver"><Archive className="size-4" /></button>}
                    </>
                  )}
                  {perms.canSupprimer && <button onClick={(ev) => act(ev, () => { if (window.confirm(`Supprimer l'engin « ${e.designation} » ?`)) deleteMutation.mutate(e.id); })} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex min-h-[30vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
          <Truck className="size-10 text-content-muted" />
          <p className="mt-3 text-sm text-content-secondary">{showArchived ? 'Aucun engin archivé.' : 'Aucun engin dans le parc.'}</p>
        </div>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={editing ? "Modifier l'engin" : (typeEngin === 'LOUE' ? 'Machine louée à un tiers' : 'Nouvel engin')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={handleSubmit((v) => saveMutation.mutate(v))} loading={saveMutation.isPending}>{editing ? 'Enregistrer' : 'Ajouter'}</Button>
          </>
        }
      >
        <div className="space-y-5">
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">Identification</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Code"><Input placeholder="Auto (ENG-001, VU-001…)" {...register('code')} disabled={!!editing} /></FormField>
              <FormField label="Catégorie">
                <select {...register('categorie')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
                  {ENGIN_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORIE_LABEL[c]}</option>)}
                </select>
              </FormField>
              <FormField label="Désignation" required className="sm:col-span-2"><Input placeholder="ex : Pelle CAT 320 / Toyota Hilux" {...register('designation')} /></FormField>
              <FormField label="Mode">
                <select {...register('type')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
                  <option value="PROPRE">Propre</option>
                  <option value="LOUE">Loué</option>
                </select>
              </FormField>
              <FormField label="Marque"><Input {...register('marque')} /></FormField>
              <FormField label="Modèle"><Input {...register('modele')} /></FormField>
              <FormField label="Immatriculation"><Input {...register('immatriculation')} /></FormField>
              <FormField label="Année de fabrication"><Input type="number" step="1" {...register('annee_fabrication')} /></FormField>
              <FormField label="Statut">
                <select {...register('statut')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
                  {ENGIN_STATUTS.map((s) => <option key={s} value={s}>{STATUT_UI[s].label}</option>)}
                </select>
              </FormField>
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">Compteur & maintenance</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField label="Compteur actuel"><Input type="number" step="1" {...register('compteur_actuel')} /></FormField>
              <FormField label="Unité">
                <select {...register('unite_compteur')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
                  <option value="H">Heures</option>
                  <option value="KM">Kilomètres</option>
                </select>
              </FormField>
              <FormField label="Conso de référence (L/h)"><Input type="number" step="0.01" placeholder="Optionnel — base de l'alerte surconso." {...register('conso_reference_lh')} /></FormField>
              <FormField label="Seuil prochaine révision"><Input type="number" step="1" {...register('prochaine_maintenance_km')} /></FormField>
              <FormField label="Prochaine révision (date)"><Input type="date" {...register('prochaine_maintenance_date')} /></FormField>
            </div>
          </section>

          {typeEngin === 'LOUE' ? (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">Location</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField label="Loueur">
                  <select {...register('loueur_id')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
                    <option value="">— Sélectionner —</option>
                    {tiers?.items.filter((t) => t.type !== 'CLIENT').map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}
                  </select>
                </FormField>
                <FormField label="Coût de location / jour (DZD)"><Input type="number" step="1" {...register('cout_location_jour')} /></FormField>
                <FormField label="Début location"><Input type="date" {...register('location_date_debut')} /></FormField>
                <FormField label="Fin location"><Input type="date" {...register('location_date_fin')} /></FormField>
              </div>
            </section>
          ) : (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">Acquisition & amortissement</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <FormField label="Date d'acquisition"><Input type="date" {...register('date_acquisition')} /></FormField>
                <FormField label="Valeur d'acquisition (DZD)"><Input type="number" step="1" {...register('valeur_acquisition')} /></FormField>
                <FormField label="Durée d'amortissement (ans)"><Input type="number" step="1" {...register('duree_amortissement_ans')} /></FormField>
              </div>
            </section>
          )}
        </div>
      </Modal>
    </>
  );
}
